/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useMemo } from 'react';

import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from '@material-ui/core/Grid';

import UserSessionContext from 'context/UserSessionContext';
import HeatmapCell from 'components/Heatmap/components/HeatmapCell';
import HeatmapBarChart from 'components/Heatmap/components/HeatmapBarChart';
import HeatmapNavigation from 'components/Heatmap/components/HeatmapNavigation';
import LoadingSpinner from 'components/LoadingSpinner';
import { useHeatmapSeries, useHeatmapDates, useFetchHeatmapData } from 'hooks/useHeatmap';
import { sortHeatmapArray, calculateHeatmapDefaultDate } from 'utils/widget-helpers';
import useApi, { UPDATE_USER_ROUTE } from 'hooks/useApi';
import * as ACTIONS from 'context/actions';

const useStyles = makeStyles({
  containerMargin: {
    marginBottom: '40px'
  }
});

const Heatmap = () => {
  const classes = useStyles();
  const { session, dispatch } = useContext(UserSessionContext);
  const { heatmapSettings, homepagePreference } = session;
  const [, sendRequest] = useApi();
  const homePageUrl = '/';

  const handleSetHomepageClick = () => {
    sendRequest(UPDATE_USER_ROUTE(homePageUrl));
    dispatch({type: ACTIONS.UPDATE_USER_HOMEPAGE_PREFERENCE, payload: homePageUrl})
  };

  const {
    groupBy,
    showIndexes,
    showPortfolios,
    showProposedManagers,
    sortBy,
    timeSeriesSetId
  } = heatmapSettings;

  const [, filteredSeries] = useHeatmapSeries();
  const [, filteredDates] = useHeatmapDates();
  const [state, fetchHeatmapData] = useFetchHeatmapData();
  const { isLoading: isHeatmapFetchLoading, data: heatmapData } = state;
  const { data, heatmapSettings: responseSettings } = heatmapData || {};
  const { sortBy: responseSortBy } = responseSettings || {};

  const sortedData = useMemo(() => {
    if (responseSortBy && data) {
      return sortHeatmapArray(responseSortBy, data);
    }
    return null;
  }, [data, responseSortBy]);

  const [series, setSeries] = useState();
  const [dates, setDates] = useState();
  const [apiData, setApiData] = useState({
    groupType: groupBy,
    seriesType: timeSeriesSetId,
    dateType: calculateHeatmapDefaultDate()
  });
  const [valueRadioValue, setValueRadioValue] = useState({
    indexes: showIndexes,
    portfolios: showPortfolios,
    showProposedManagers
  });
  const [sortType, setSortType] = useState(sortBy);
  const [isHeatmap, setIsHeatmap] = useState(true);
  const [displayType, setDisplayType] = useState('heatmap');

  const handleSortChange = event => {
    setSortType(event.target.value);
  };

  const handleApiParamsChange = params => {
    if (params.target) {
      setApiData(prevState => ({ ...prevState, groupType: params.target.value }));
      return;
    }

    if (params.value && params.type) {
      setApiData(prevState => ({ ...prevState, [params.type]: params.value }));
    }
  };

  const handleRadioChange = event => {
    const { name, value } = event;

    setValueRadioValue({
      ...valueRadioValue,
      [name]: value
    });
  };

  const handleDisplayChange = event => {
    setDisplayType(event.target.value);
  };

  const handleBarChartClick = () => {
    if (sortType === 'name') {
      setSortType('value');
    }
    setIsHeatmap(false);
  };

  const handleHeatmapClick = () => {
    setIsHeatmap(true);
  };

  useEffect(() => {
    if (filteredDates) {
      setDates(filteredDates);
    }
  }, [filteredDates]);

  useEffect(() => {
    if (filteredSeries) {
      setSeries(filteredSeries);
    }
  }, [filteredSeries]);

  useEffect(() => {
    fetchHeatmapData({
      timeSeriesSetId: apiData.seriesType,
      date: apiData.dateType,
      groupBy: apiData.groupType,
      sortBy: sortType,
      showPortfolios: valueRadioValue.portfolios,
      showIndexes: valueRadioValue.indexes,
      showProposedManagers: valueRadioValue.showProposedManagers
    });
  }, [apiData, valueRadioValue, sortType]);

  return (
    <Grid
      container
      direction="row"
      justify="flex-start"
      alignItems="center"
      className={classes.containerMargin}
    >
      <HeatmapNavigation
        handleHeatmapClick={handleHeatmapClick}
        isHeatmap={isHeatmap}
        handleBarChartClick={handleBarChartClick}
        dates={dates}
        dateType={apiData.dateType}
        handleApiParamsChange={handleApiParamsChange}
        series={series}
        seriesType={apiData.seriesType}
        groupType={apiData.groupType}
        sortType={sortType}
        displayType={displayType}
        handleSortChange={handleSortChange}
        handleDisplayChange={handleDisplayChange}
        valueRadioValue={valueRadioValue}
        handleRadioChange={handleRadioChange}
        setHeatmapAsHome={handleSetHomepageClick}
        homepage={homepagePreference}
      />
      {isHeatmapFetchLoading && <LoadingSpinner size={65} />}

      {data &&
        sortedData &&
        !isHeatmapFetchLoading &&
        isHeatmap &&
        sortedData.map(cell => (
          <Grid item xs="auto" key={`${cell.name}- ${cell.value} - ${cell.timeSeriesId}`}>
            <HeatmapCell {...cell} date={apiData.dateType} userRole={session.user.role}/>
          </Grid>
        ))}

      {!isHeatmap && !isHeatmapFetchLoading && (
        <Grid item xs={12}>
          <HeatmapBarChart data={sortedData} sortType={sortType} />
        </Grid>
      )}
    </Grid>
  );
};

export default Heatmap;
